import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect, useState, useContext, useCallback } from "react";
import { ThemeContext } from "../../../../theme/ThemeContext";
import DocumentService from "../../../../services/DocumentService";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import { Skeleton } from "primereact/skeleton";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


const roundedBarsPlugin = {
  id: 'roundedBars',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      
      // Create gradient for each render
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      gradient.addColorStop(0, "#b1aef8"); 
      gradient.addColorStop(1, "#5A57FF");
      
      meta.data.forEach((bar, index) => {
        const { x, y, width, base } = bar;
        const radius = 6;
        
        ctx.save();
        ctx.fillStyle = gradient; 
      
        ctx.beginPath();
        ctx.moveTo(x - width / 2 + radius, base);
        ctx.lineTo(x + width / 2 - radius, base);
        ctx.quadraticCurveTo(x + width / 2, base, x + width / 2, base - radius);
        ctx.lineTo(x + width / 2, y + radius);
        ctx.quadraticCurveTo(x + width / 2, y, x + width / 2 - radius, y);
        ctx.lineTo(x - width / 2 + radius, y);
        ctx.quadraticCurveTo(x - width / 2, y, x - width / 2, y + radius);
        ctx.lineTo(x - width / 2, base - radius);
        ctx.quadraticCurveTo(x - width / 2, base, x - width / 2 + radius, base);
        ctx.closePath();
        ctx.fill();
        
        ctx.restore();
      });
    });
    
    return false; 
  }
};

export default function DocumentBarChart() {
  const chartRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const authContext = useContext(AuthContext);
  const isDark = theme === "dark";

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Total Documents",
        data: [],
        backgroundColor: "#5A57FF",
        borderRadius: 0,
        barThickness: 16,
        maxBarThickness: 20,
      },
    ],
  });

  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("all");

const loadDashboard = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const response = await DocumentService.GetCourseDashboard(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (response.isSuccess && Array.isArray(response.data)) {
        setCourses(response.data);
        const labels = response.data.map(m => m.courseCode);
        const values = response.data.map(m => m.documentCount);

        setChartData({
          labels,
          datasets: [
            {
              label: "Total Documents",
              data: values,
              backgroundColor: "#5A57FF",
              borderRadius: 0,
              barThickness: 16,
              maxBarThickness: 20,
            },
          ],
        });
      } else {
        AlertService.error(
          response.errorMessage || "Failed to fetch dashboard data"
        );
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
      AlertService.error("Failed to fetch dashboard data");
      console.error(err);
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

  useEffect(() => {
    const controller = new AbortController(); 
    loadDashboard(controller.signal); 
    return () => controller.abort(); 
  }, [loadDashboard]);

  const updateChart = useCallback((allCourses, courseId) => {
    if (courseId === "all") {
      const labels = allCourses.map(m => m.courseCode);
      const values = allCourses.map(m => m.documentCount);
      setChartData(prev => ({
        ...prev,
        labels,
        datasets: [{ 
          ...prev.datasets[0], 
          data: values,
          borderRadius: 0
        }],
      }));
    } else {
      const filtered = allCourses.filter(c => String(c.courseId) === courseId);
      const labels = filtered.map(m => m.courseCode);
      const values = filtered.map(m => m.documentCount);
      setChartData(prev => ({
        ...prev,
        labels,
        datasets: [{ 
          ...prev.datasets[0], 
          data: values,
          borderRadius: 0
        }],
      }));
    }
  }, []);

  const handleCourseChange = e => {
    const value = e.target.value;
    setSelectedCourse(value);
    updateChart(courses, value);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        titleFont: { family: "Poppins", size: 13, weight: "600" },
        bodyFont: { family: "Poppins", size: 12 },
        callbacks: {
          title: tooltipItems => {
            const idx = tooltipItems[0].dataIndex;
            const item =
              selectedCourse === "all"
                ? courses[idx]
                : courses.find(c => String(c.courseId) === selectedCourse);
            return item?.courseName || "";
          },
          label: tooltipItems => {
            return `Documents: ${tooltipItems.raw}`;
          },
        },
      },
      roundedBars: true,
    },
    scales: {
      x: {
        ticks: {
          color: isDark ? "#eaeaea" : "#444444",
          font: { size: 12, family: "Poppins" },
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          color: isDark ? "#eaeaea" : "#444444",
          font: { size: 12, family: "Poppins" },
        },
        grid: { color: isDark ? "#444444" : "#f1f1f1" },
      },
    },
  };

  return (
    <div
      className="charts d-flex flex-column"
      style={{
        padding: 16,
        height: "336px",
        width: "100%",
        background: isDark ? "#1e1e1e" : "#fff",
        border: isDark ? "1px solid #444" : "1px solid #eee",
        borderRadius: "12px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {loading ? (
          <Skeleton width="120px" height="20px" />
        ) : (
          <h6 style={{ margin: 0, fontSize: 16 }}>Uploaded documents</h6>
        )}
        {loading ? (
          <Skeleton width="140px" height="32px" />
        ) : (
          <select
            value={selectedCourse}
            onChange={handleCourseChange}
            style={{
              border: "1px solid #ddd",
              backgroundColor: isDark ? "#222" : "#fff",
              color: isDark ? "#eaeaea" : "#000",
              borderRadius: 6,
              padding: "4px 20px 4px 8px",
              fontSize: 13,
              maxWidth: "140px",
              width: "140px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              appearance: "auto",
              cursor: "pointer",
            }}
            title={selectedCourse === "all" ? "All Courses" : courses.find(c => String(c.courseId) === selectedCourse)?.courseName || ""}
          >
            <option value="all" title="All Courses">
              All Courses
            </option>
            {courses.map(c => (
              <option key={c.courseId} value={c.courseId} title={c.courseName}>
                {c.courseName}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Chart / Skeleton */}
      <div style={{ flexGrow: 1, position: "relative" }}>
        {loading ? (
          <div className=" d-flex align-items-end gap-2 px-2 py-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                height={`${Math.max(20, 70 + i * 30)}px`}
                className="rounded-top"
              />
            ))}
          </div>
        ) : (
          <Bar ref={chartRef} data={chartData} options={options} plugins={[roundedBarsPlugin]} />
        )}
      </div>
    </div>
  );
}